// src/components/DictWeaponTable/DictWeaponTable.tsx
import React, { memo } from 'react';
import {
    styled,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DictWeapon, DictWeaponProfile, DictWeaponUpgradeRef } from '../../model/Dto/Dict/Weapon';
import {StyledTable} from "../../components/FighterCard/StyledTable";
import ItemsList from "../../components/ItemsList";

interface DictWeaponTableProps {
    weapons: DictWeapon[];
    onEdit: (weapon: DictWeapon) => void;
    onDelete: (weapon: DictWeapon) => void;
}

export const DictWeaponTable: React.FC<DictWeaponTableProps> = memo(({ weapons, onEdit, onDelete }) => {
    if (!weapons.length) return null;

    return (
        <TableContainer>
            <StyledTable size="small">
                <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '10%' }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <CellWithNoBorder colSpan={1} />
                        <CellWithNoBorder align="center" colSpan={2}>
                            Range
                        </CellWithNoBorder>
                        <CellWithNoBorder align="center" colSpan={2}>
                            Acc
                        </CellWithNoBorder>
                        <CellWithNoBorder colSpan={5} />
                        <CellWithNoBorder />
                    </TableRow>
                    <TableRow>
                        <CellWithRightBorder>Weapon</CellWithRightBorder>
                        <CellWithNoBorder align="center">S</CellWithNoBorder>
                        <CellWithRightBorder align="center">L</CellWithRightBorder>
                        <CellWithNoBorder align="center">S</CellWithNoBorder>
                        <CellWithRightBorder align="center">L</CellWithRightBorder>
                        <CellWithRightBorder align="center">Str</CellWithRightBorder>
                        <CellWithRightBorder align="center">Ap</CellWithRightBorder>
                        <CellWithRightBorder align="center">D</CellWithRightBorder>
                        <CellWithRightBorder align="center">Am</CellWithRightBorder>
                        <CellWithNoBorder>Traits</CellWithNoBorder>
                        <CellWithRightBorder align="center">Actions</CellWithRightBorder>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <ItemsList
                        items={weapons}
                        renderItem={(weapon: DictWeapon, index: number) => (
                            <WeaponInfo
                                key={weapon.id}
                                weapon={weapon}
                                index={index}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        )}
                    />
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
});

interface WeaponInfoProps {
    weapon: DictWeapon;
    index: number;
    onEdit: (weapon: DictWeapon) => void;
    onDelete: (weapon: DictWeapon) => void;
}

function WeaponInfo({ weapon, index, onEdit, onDelete }: WeaponInfoProps) {
    return (
        <>
            <WeaponRow weapon={weapon} index={index} onEdit={onEdit} onDelete={onDelete} />
            <UpgradesRow upgrades={weapon.upgrades} index={index} />
        </>
    );
}

interface WeaponRowProps {
    weapon: DictWeapon;
    index: number;
    onEdit: (weapon: DictWeapon) => void;
    onDelete: (weapon: DictWeapon) => void;
}

function WeaponRow({ weapon, index, onEdit, onDelete }: WeaponRowProps) {
    const profiles = weapon.profiles;

    // Кнопки действий
    const actionButtons = (
        <>
            <IconButton onClick={() => onEdit(weapon)} size="small" color="primary">
                <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => onDelete(weapon)} size="small" color="error">
                <DeleteIcon fontSize="small" />
            </IconButton>
        </>
    );

    // Если профилей больше одного, отображаем строку-заголовок с названием оружия и кнопками
    if (profiles.length > 1) {
        return (
            <>
                <Stroke
                    items={[
                        weapon.name,
                        '', '', '', '', '', '', '', '', '',
                        actionButtons,
                    ]}
                    index={index}
                    isHeader={true}
                />
                <ItemsList
                    items={profiles}
                    renderItem={(profile: DictWeaponProfile, keyIndex: number) => (
                        <Stroke
                            key={keyIndex}
                            items={[
                                profile.name,
                                profile.sr ?? '-',
                                profile.lr ?? '-',
                                profile.sm ?? '-',
                                profile.lm ?? '-',
                                profile.str ?? '-',
                                profile.ap ?? '-',
                                profile.d ?? '-',
                                profile.am ?? '-',
                                profile.traits.map((t) => t.name).join(', '),
                                null, // кнопки не дублируем
                            ]}
                            index={index}
                            isHeader={false}
                        />
                    )}
                />
            </>
        );
    }

    // Один профиль: отображаем одну строку с названием оружия и кнопками
    const profile = profiles[0];
    return (
        <Stroke
            items={[
                weapon.name,
                profile?.sr ?? '-',
                profile?.lr ?? '-',
                profile?.sm ?? '-',
                profile?.lm ?? '-',
                profile?.str ?? '-',
                profile?.ap ?? '-',
                profile?.d ?? '-',
                profile?.am ?? '-',
                profile?.traits.map((t) => t.name).join(', '),
                actionButtons,
            ]}
            index={index}
            isHeader={true}
        />
    );
}

interface UpgradesRowProps {
    upgrades: DictWeaponUpgradeRef[];
    index: number;
}

function UpgradesRow({ upgrades, index }: UpgradesRowProps) {
    if (upgrades.length === 0) return null;
    const upgradeNames = upgrades.map((u) => u.name).join(', ');
    return (
        <Stroke
            items={[
                `Upgrades: ${upgradeNames}`,
                '', '', '', '', '', '', '', '', '',
                null,
            ]}
            index={index}
            isHeader={true}
        />
    );
}

// Стилизованные ячейки
const CellWithNoBorder = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { fontWeight: '600' },
    borderWidth: 0,
}));

const CellWithRightBorder = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { fontWeight: '600' },
    borderWidth: 0,
    borderRightWidth: 1,
    borderRightColor: theme.palette.grey[500],
    borderRightStyle: 'solid',
}));

interface StrokeProps {
    items: (string | React.ReactNode | null)[];
    index: number;
    isHeader: boolean;
}

function Stroke({ items, index, isHeader }: StrokeProps) {
    return (
        <RowWithBackground index={index}>
            <CellWithRightBorder component="th" scope="row">
                {isHeader ? items[0] : `- ${items[0]}`}
            </CellWithRightBorder>
            <CellWithNoBorder align="center">{items[1]}</CellWithNoBorder>
            <CellWithRightBorder align="center">{items[2]}</CellWithRightBorder>
            <CellWithNoBorder align="center">{items[3]}</CellWithNoBorder>
            <CellWithRightBorder align="center">{items[4]}</CellWithRightBorder>
            <CellWithRightBorder align="center">{items[5]}</CellWithRightBorder>
            <CellWithRightBorder align="center">{items[6]}</CellWithRightBorder>
            <CellWithRightBorder align="center">{items[7]}</CellWithRightBorder>
            <CellWithRightBorder align="center">{items[8]}</CellWithRightBorder>
            <CellWithNoBorder>{items[9]}</CellWithNoBorder>
            <CellWithRightBorder align="center">{items[10]}</CellWithRightBorder>
        </RowWithBackground>
    );
}

function RowWithBackground({ index, children }: { index: number; children: React.ReactNode }) {
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        backgroundColor: index % 2 === 1 ? 'inherit' : theme.palette.action.hover,
    }));
    return <StyledTableRow>{children}</StyledTableRow>;
}